import { addDays, CalendarResponse, todayIn, type CalendarDay, type User } from '@squats/shared';
import { useCachedApi } from './cache.ts';
import { calendarPath } from './log/sync.ts';
import { api } from './api.ts';
import { useApi } from './useApi.ts';

export type Calendar = CalendarResponse;
export type CalendarSplit = CalendarResponse['splits'][number];

export function useCalendar(from: string, to: string) {
  return useApi(() => api(`/api/calendar?from=${from}&to=${to}`, CalendarResponse), [from, to]);
}

/** A stable colour (0–5) per split, so the same workout reads the same across the calendar. */
export function splitTone(splitId: string): number {
  let h = 0;
  for (const ch of splitId) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % 6;
}

export function dayOf(cal: Calendar, userId: string, date: string): CalendarDay | undefined {
  return cal.days.find((d) => d.userId === userId && d.date === date);
}

export const splitOf = (cal: Calendar, id: string | null) => (id ? cal.splits.find((s) => s.id === id) : undefined);
export const nameOf = (cal: Calendar, userId: string) => cal.users.find((u) => u.id === userId)?.displayName ?? 'Someone';

/** The plan for a day as a coloured badge. */
export function PlanBadge({ cal, day }: { cal: Calendar; day: CalendarDay }) {
  if (day.kind === 'split') {
    const split = splitOf(cal, day.splitId);
    return <span className={`plan tone-${splitTone(day.splitId!)}`}>{split?.name ?? 'Workout'}</span>;
  }
  const label = { rest: 'Rest', skip: 'Skipped', unplanned: 'No plan' }[day.kind];
  return <span className={`plan plain ${day.kind}`}>{label}</span>;
}

/** "with Remy" / "with Remy and Sam". */
export function togetherText(cal: Calendar, day: CalendarDay): string | null {
  if (!day.together.length) return null;
  const names = day.together.map((id) => nameOf(cal, id));
  return `with ${names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}` : names[0]}`;
}

/** The planner (an admin) changes plans, for anyone. Everyone else sees them. */
export const isPlanner = (user: Pick<User, 'role'>) => user.role === 'admin';

/** An API path about someone else's plan carries `?user=` (the planner editing for them). */
export const forUser = (path: string, subjectId: string, me: Pick<User, 'id'>) => (subjectId === me.id ? path : `${path}${path.includes('?') ? '&' : '?'}user=${subjectId}`);

/** Someone's planned day, from the saved week calendar when it covers the date (so it works offline), else fetched. */
export function usePlannedDay(user: User, userId: string, date: string): CalendarDay | undefined {
  return usePlannedDays(user, date)(userId);
}

/** Everyone's planned day: a lookup by person (same sources as usePlannedDay). */
export function usePlannedDays(user: User, date: string): (userId: string) => CalendarDay | undefined {
  const today = todayIn(user.timezone);
  const inWeek = date >= today && date <= addDays(today, 6);
  const { data } = useCachedApi(user.id, inWeek ? calendarPath(today, addDays(today, 6)) : calendarPath(date, date), CalendarResponse);
  return (userId) => (data ? dayOf(data, userId, date) : undefined);
}
